import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, ArrowUpRight, Check } from "lucide-react";
import { toast } from "sonner";
import { formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export const PackageCard = ({ pkg, index = 0 }) => {
  const { addItem } = useCart();
  const [added, setAdded] = useState(false);

  const handleAdd = () => {
    addItem(pkg, 1);
    setAdded(true);
    toast.success("Aggiunto al carrello", { description: pkg.title });
    setTimeout(() => setAdded(false), 1600);
  };

  return (
    <motion.article
      data-testid={`package-card-${pkg.slug}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, delay: (index % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-lineblue bg-panel transition-all duration-500 hover:border-glow hover:bg-panelhover"
    >
      <Link to={`/pacchetti/${pkg.slug}`} data-testid={`view-details-${pkg.slug}`} className="relative block aspect-[16/10] overflow-hidden">
        <img
          src={pkg.image}
          alt={pkg.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-panel via-panel/20 to-transparent" />
        {pkg.popular && (
          <span className="absolute left-4 top-4 rounded-full bg-electric px-3 py-1 font-mono text-[9px] font-medium uppercase tracking-[0.2em] text-ink">
            {pkg.tag}
          </span>
        )}
      </Link>

      <div className="flex flex-1 flex-col p-6 pt-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-glow">
          {pkg.category}
        </p>
        <Link to={`/pacchetti/${pkg.slug}`}>
          <h3 className="mt-2 font-display text-xl font-semibold leading-snug text-white transition-colors duration-300 group-hover:text-electric">
            {pkg.title}
          </h3>
        </Link>
        <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-slate-400">
          {pkg.short_desc}
        </p>

        <div className="mt-6 flex items-end justify-between gap-4 border-t border-lineblue pt-5">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">
              A partire da
            </p>
            <p className="font-display text-2xl font-bold text-white">
              {formatPrice(pkg.price)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid={`add-to-cart-${pkg.slug}`}
              onClick={handleAdd}
              aria-label={`Aggiungi ${pkg.title} al carrello`}
              className={`flex h-11 w-11 items-center justify-center rounded-full border transition-all duration-300 ${
                added
                  ? "border-electric bg-electric text-ink"
                  : "border-glow/50 text-electric hover:bg-electric hover:text-ink"
              }`}
            >
              {added ? <Check className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
            </button>
            <Link
              to={`/pacchetti/${pkg.slug}`}
              data-testid={`open-${pkg.slug}`}
              aria-label={`Dettagli ${pkg.title}`}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-lineblue text-slate-300 transition-all duration-300 hover:border-glow hover:text-electric"
            >
              <ArrowUpRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

export default PackageCard;
